import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { AppPanelComponent } from '@datachain/ui-sdk/components/app-panel';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	firstValueFrom,
	map,
	Observable,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import {
	AppSelector,
	getAvailableMarketplaceMembers,
	resetAvailableMarketplaceMembers,
} from '../../../../core/store';
import { DcIcons } from '../../../../ui/app-dc.icons';
import {
	AppMemberListComponent,
	MemberListGridOptions,
} from '../../../../ui/components/app-member-list/app-member-list.component';
import {
	MemberItemEntity,
	SelectedMember,
} from '../../../../ui/components/app-member-list/member-item.entity';
import { MemberListEntity } from '../../../../ui/components/app-member-list/member-list.entity';
import {
	AccessConsumerMappingPopupProvider,
	ConsumerType,
} from './access-consumer-mapping-popup.provider';

@Component({
	selector: 'app-access-consumer-mapping-popup',
	templateUrl: './access-consumer-mapping-popup.component.html',
	styleUrls: ['./access-consumer-mapping-popup.component.scss'],
	providers: [AccessConsumerMappingPopupProvider],
})
export class AccessConsumerMappingPopupComponent<
		T extends { selectedConsumers: Map<number, SelectedMember> }
	>
	extends DcBaseComponent
	implements OnInit, OnDestroy
{
	protected readonly DcIcons = DcIcons;
	protected readonly ConsumerType = ConsumerType;

	public consumerTypes: Array<ButtonToggle> = [
		{
			label: $localize`:i18n=@@exposition.access.consumer.type.users:`,
			id: 'get-users',
			isSelected: true,
			icon: DcIcons.User,
			cb: (): void => this.consumerType$.next(ConsumerType.User),
		},
		{
			label: $localize`:i18n=@@exposition.access.consumer.type.groups:`,
			id: 'get-groups',
			isSelected: false,
			icon: DcIcons.Group,
			cb: (): void => this.consumerType$.next(ConsumerType.Group),
		},
	];

	public consumerType$ = new BehaviorSubject<ConsumerType>(ConsumerType.User);
	public vo$: Observable<{
		consumerList: MemberListEntity;
		selectedConsumers: Array<SelectedMember>;
		initialSelectedConsumers: Map<number, SelectedMember>;
	}>;

	private readonly selectedConsumers$ = new BehaviorSubject<
		Array<SelectedMember>
	>(this.modalData.selectedConsumers.toList().toArray());

	private readonly initialSelectedConsumers$ = new BehaviorSubject<
		Map<number, SelectedMember>
	>(this.modalData.selectedConsumers);

	@ViewChild(AppPanelComponent)
	private readonly appPanelCmp: AppPanelComponent | null = null;

	@ViewChild(AppMemberListComponent)
	private readonly appMemberListCmp: AppMemberListComponent | null = null;

	public constructor(
		private readonly store: Store,
		private readonly appSelector: AppSelector,
		private readonly modalService: AppModalService,
		private readonly consumerMappingProvider: AccessConsumerMappingPopupProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'access-consumer-mapping-popup';

		const consumerList$ = this.appSelector
			.getAvailableMarketplaceMembers$()
			.pipe(
				withLatestFrom(
					this.consumerType$,
					this.appSelector.getTotalAvailableMarketplaceMembers$()
				),
				map(([elements, consumerType, total]) => {
					const items = elements.map((item) =>
						MemberItemEntity.buildMember({
							id: item.id,
							creationDate: item.creationDate,
							updateDate: item.updateDate,
							login: item.login,
							firstName: item.firstName,
							lastName: item.lastName,
							mail: item.mail,
							avatar: item.avatar,
							name: item.name,
							description: item.description,
							isGroup: item.isGroup,
						})
					);
					return this.consumerMappingProvider.setListElements(
						consumerType,
						items,
						total
					);
				})
			);

		this.vo$ = combineAll({
			consumerList: consumerList$,
			selectedConsumers: this.selectedConsumers$,
			initialSelectedConsumers: this.initialSelectedConsumers$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.store.dispatch(resetAvailableMarketplaceMembers());
	}

	public async onOptionChange(option: MemberListGridOptions): Promise<void> {
		const consumerType = await firstValueFrom(this.consumerType$);

		if (consumerType === ConsumerType.User) {
			this.store.dispatch(
				getAvailableMarketplaceMembers({
					consumerType,
					page: option.page,
					searchTerm: option.searchTerm,
					filterFields: option.filterFields,
					sortFields: option.sortFields,
				})
			);
			return;
		}
		this.store.dispatch(
			getAvailableMarketplaceMembers({
				consumerType,
				page: option.page,
				searchTerm: option.searchTerm,
				filterFields: option.filterFields,
				sortFields: option.sortFields,
			})
		);
	}

	public selectTargetConsumers(selectedMembers: Array<SelectedMember>): void {
		this.selectedConsumers$.next(selectedMembers);
	}

	public async removeSelectedConsumer(id: number): Promise<void> {
		if (!this.appMemberListCmp) {
			return;
		}
		await this.appMemberListCmp.removeSelectedMember(id);
	}

	public search(searchTerm: string): void {
		if (!this.appMemberListCmp) {
			return;
		}
		this.appMemberListCmp.search(searchTerm.trim());
	}

	public refresh(): void {
		if (!this.appMemberListCmp) {
			return;
		}
		this.appMemberListCmp.refresh();
	}

	public async saveAction(): Promise<void> {
		const selectedConsumers = await firstValueFrom(this.selectedConsumers$);

		this.modalService.closeActiveModal({
			selectedConsumers: Map(selectedConsumers.map((item) => [item.id, item])),
		});
	}

	private handleSubscription(): void {
		this.consumerType$
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					if (this.appPanelCmp) {
						// this.appPanelCmp.clearSearch(new MouseEvent('click'));
					}

					this.appMemberListCmp?.resetGrid();
				})
			)
			.subscribe();
	}
}
